import { Button, Paper, Typography } from '@mui/material';
import moment from 'moment';
import { HourRange, formatShiftSummary } from './HourRangePicker';
import { useStyles } from './ScheduleStyles';
import { useLocale } from '../../i18n/LocaleContext';

export type ShiftChange = {
  id: string;
  at: string;
  personA: string;
  dayA: string;
  rangeA: HourRange;
  personB: string;
  dayB: string;
  rangeB: HourRange;
};

type ShiftChangeHistoryProps = {
  changes: ShiftChange[];
  onUndo: (change: ShiftChange) => void;
};

const formatDay = (dayKey: string, locale: string) =>
  moment(dayKey).locale(locale).format('ddd D MMM');

const formatWho = (
  person: string,
  dayKey: string,
  locale: string,
  hidePerson = false,
) =>
  hidePerson
    ? formatDay(dayKey, locale)
    : `${person} · ${formatDay(dayKey, locale)}`;

const ShiftChangeHistory = ({ changes, onUndo }: ShiftChangeHistoryProps) => {
  const classes = useStyles();
  const { locale, t } = useLocale();

  return (
    <Paper className={classes.historyPaper}>
      <Typography variant="h6">{t('shiftChanges')}</Typography>
      {changes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('noSwaps')}
        </Typography>
      ) : (
        <table className={classes.historyTable}>
          <thead>
            <tr>
              <th className={classes.historyHead}>{t('when')}</th>
              <th className={classes.historyHead}>{t('from')}</th>
              <th className={classes.historyHead}>{t('before')}</th>
              <th className={classes.historyHead}>{t('after')}</th>
              <th className={`${classes.historyHead} ${classes.historySwap}`}>
                ↔
              </th>
              <th className={classes.historyHead}>{t('to')}</th>
              <th className={classes.historyHead}>{t('before')}</th>
              <th className={classes.historyHead}>{t('after')}</th>
              <th className={classes.historyHead} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {changes.map((change) => {
              const samePerson = change.personA === change.personB;

              return (
                <tr key={change.id} className={classes.historyRow}>
                  <td className={classes.historyTime}>
                    {moment(change.at).locale(locale).format('D MMM HH:mm')}
                  </td>
                  <td className={classes.historyWho}>
                    {formatWho(change.personA, change.dayA, locale)}
                  </td>
                  <td className={classes.historyHours}>
                    {formatShiftSummary(change.rangeA, t)}
                  </td>
                  <td className={classes.historyHoursAfter}>
                    {formatShiftSummary(change.rangeB, t)}
                  </td>
                  <td className={classes.historySwap} aria-hidden>
                    ↔
                  </td>
                  <td className={classes.historyWho}>
                    {formatWho(change.personB, change.dayB, locale, samePerson)}
                  </td>
                  <td className={classes.historyHours}>
                    {formatShiftSummary(change.rangeB, t)}
                  </td>
                  <td className={classes.historyHoursAfter}>
                    {formatShiftSummary(change.rangeA, t)}
                  </td>
                  <td className={classes.historyAction}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => onUndo(change)}
                    >
                      {t('undo')}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Paper>
  );
};

export default ShiftChangeHistory;
